import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * Overrides AfriMarket meta tags with Plyx-only branding. Mounted on every
 * Plyx route so the browser tab, OG previews, and search engines all read
 * "Plyx" — never "AfriMarket". Especially important in standalone preview
 * mode where the page must feel like a separate product.
 *
 * Defense-in-depth: the useEffect imperatively removes any meta tags or
 * JSON-LD blocks containing the string "afrimarket" — guards against any
 * third-party script or stale SSR/CRA injection that bypasses Helmet.
 */
const PlyxSEO = ({ title = "Plyx — Where moments become movements", description = "Plyx — the social-commerce super-app. Watch, sell, earn, connect across every culture." }) => {
  useEffect(() => {
    try {
      // 1. Strip any meta / JSON-LD / link tag whose content references afrimarket.
      //    Belt-and-braces — protects against third-party scripts re-injecting them.
      const tagSel = "meta[property^='og:'], meta[name^='twitter:'], meta[name='keywords'], meta[name='author'], meta[name='apple-mobile-web-app-title'], meta[name='application-name'], meta[name='description'], script[type='application/ld+json']";
      document.querySelectorAll(tagSel).forEach((el) => {
        const text = (el.getAttribute("content") || el.textContent || "").toLowerCase();
        if (text.includes("afrimarket")) el.remove();
      });
      // 2. Swap the PWA manifest + favicon to Plyx versions so the browser tab
      //    title, bookmark name, and Add-to-Home-Screen name all read "Plyx".
      const swapHref = (selector, newHref) => {
        document.querySelectorAll(selector).forEach((el) => el.setAttribute("href", newHref));
      };
      swapHref("link[rel='manifest']", "/plyx-manifest.json");
      swapHref("link[rel='icon']", "/plyx-favicon.svg");
      swapHref("link[rel='apple-touch-icon']", "/plyx-favicon.svg");
      swapHref("link[rel='mask-icon']", "/plyx-favicon.svg");
    } catch { /* never break the page */ }
  });
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content="Plyx, social commerce, short video, creator economy, watch to earn, vertical video, TikTok alternative" />
      <meta name="author" content="Plyx" />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Plyx" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content="https://plyx.co.uk/" />
      <meta property="og:image" content="https://plyx.co.uk/plyx-og.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Plyx — Where moments become movements" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content="https://plyx.co.uk/plyx-og.png" />
      <meta name="twitter:site" content="@plyxapp" />

      <meta name="apple-mobile-web-app-title" content="Plyx" />
      <meta name="application-name" content="Plyx" />
      <meta name="theme-color" content="#0A0A0A" />

      <link rel="canonical" href="https://plyx.co.uk/" />
      <link rel="manifest" href="/plyx-manifest.json" />
      <link rel="icon" type="image/svg+xml" href="/plyx-favicon.svg" />
      <link rel="apple-touch-icon" href="/plyx-favicon.svg" />
    </Helmet>
  );
};

export default PlyxSEO;
